import { useEffect, useState, ReactNode } from 'react'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { useAppLockStore } from '@/stores/app-lock.store'
import { useAuthStore } from '@/stores/auth.store'
import { LockScreen } from './LockScreen'
import { PinSetup } from './PinSetup'

interface AppLockProviderProps {
  children: ReactNode
}

export function AppLockProvider({ children }: AppLockProviderProps) {
  const user = useAuthStore((state) => state.user)
  const accessToken = useAuthStore((state) => state.accessToken)
  const isAuthInitialized = useAuthStore((state) => state.isInitialized)
  const {
    isLocked,
    isSetupComplete,
    isInitialized: isAppLockInitialized,
    initialize,
    checkAndLock,
    setLastActive,
  } = useAppLockStore()

  const [showPinSetup, setShowPinSetup] = useState(false)
  const [hasShownLockThisSession, setHasShownLockThisSession] = useState(false)

  // Initialize app lock state ONLY after auth is ready and user is logged in
  useEffect(() => {
    // Wait for auth to initialize and user to be logged in with a token
    if (isAuthInitialized && user && accessToken && !isAppLockInitialized) {
      console.log('AppLockProvider: Auth ready, initializing app lock...')
      initialize()
    }
  }, [isAuthInitialized, user, accessToken, isAppLockInitialized, initialize])

  // Show PIN setup after first login if not set up
  useEffect(() => {
    if (user && isAppLockInitialized && !isSetupComplete && !showPinSetup) {
      // Small delay so user sees they're logged in first
      const timer = setTimeout(() => {
        setShowPinSetup(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [user, isAppLockInitialized, isSetupComplete, showPinSetup])

  // Check if app should be locked on initial load (timeout-based)
  useEffect(() => {
    if (user && isAppLockInitialized && isSetupComplete && !hasShownLockThisSession) {
      checkAndLock()
      setHasShownLockThisSession(true)
    }
  }, [user, isAppLockInitialized, isSetupComplete, hasShownLockThisSession, checkAndLock])

  // Listen for app state changes (foreground/background)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const setupListeners = async () => {
      const stateListener = await App.addListener('appStateChange', async ({ isActive }: { isActive: boolean }) => {
        if (!isActive) {
          // Going to background - save the current time
          await setLastActive()
        } else {
          // Coming to foreground - check if timeout has passed
          await checkAndLock()
        }
      })

      return () => {
        stateListener.remove()
      }
    }

    let cleanup: (() => void) | undefined

    setupListeners().then((cleanupFn) => {
      cleanup = cleanupFn
    })

    return () => {
      cleanup?.()
    }
  }, [checkAndLock, setLastActive])

  // If not logged in, just render children (login page)
  if (!user) {
    return <>{children}</>
  }

  // Show PIN setup if user hasn't set up PIN yet (required)
  if (showPinSetup && !isSetupComplete) {
    return (
      <PinSetup
        onComplete={() => {
          setShowPinSetup(false)
          setHasShownLockThisSession(true) // Don't lock immediately after setup
        }}
      />
    )
  }

  // Show lock screen if locked and PIN is set up
  if (isLocked && isSetupComplete) {
    return <LockScreen />
  }

  // Normal app render
  return <>{children}</>
}
